import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import ModuleShell from "@/components/modules/ModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, Gem, Shield, TrendingUp, CheckCircle2, BarChart3, Target, Layers, Clock } from "lucide-react";

type Step = "intro" | "allocation" | "risk_return" | "tax_efficiency" | "preservation" | "scenario" | "quiz" | "summary";
const STEPS: Step[] = ["intro", "allocation", "risk_return", "tax_efficiency", "preservation", "scenario", "quiz", "summary"];

const WealthStrategyModule = () => {
  const navigate = useNavigate();
  const { moduleGates, loading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();
  const [step, setStep] = useState<Step>("intro");
  const [stockPct, setStockPct] = useState(60);
  const [scenarioChoice, setScenarioChoice] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const gate = moduleGates.find(g => g.moduleKey === "wealth_strategy");
  const isLocked = !gate?.isUnlocked;
  if (loading) return null;

  const bondPct = 100 - stockPct;
  const expectedReturn = (stockPct * 0.10 + bondPct * 0.04).toFixed(1);
  const riskLevel = stockPct > 80 ? "Aggressive" : stockPct > 50 ? "Balanced" : "Conservative";

  return (
    <ModuleShell
      title="Wealth Strategy"
      stepIndex={STEPS.indexOf(step)}
      totalSteps={STEPS.length}
      isLocked={isLocked}
      lockMessage={gate?.missingPrereqs?.length ? `Complete: ${gate.missingPrereqs.join(", ")}` : "Complete Advanced Financial Strategy to unlock."}
      isCompleted={completed}
      completedMessage="You understand asset allocation, tax efficiency, and capital preservation."
      stepKey={step}
    >
      {step === "intro" && (
        <ModuleStepCard
          icon={<Gem className="w-8 h-8 text-primary" />}
          title="Wealth Strategy"
          body="Building wealth requires balancing risk, return, and tax efficiency across multiple accounts. This module ties together everything you've learned into a cohesive strategy."
          highlights={["Allocation beats stock picking", "Tax drag silently erodes returns", "Preservation is as important as growth"]}
          action={<Button onClick={() => setStep("allocation")} className="w-full gap-2">Let's Begin <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "allocation" && (
        <div className="space-y-6">
          <ModuleStepCard
            icon={<BarChart3 className="w-8 h-8 text-primary" />}
            title="Asset Allocation"
            body="The single most important investment decision is how you split between stocks and bonds. Stocks grow faster but are volatile. Bonds are stable but grow slower."
            subtext={`Stocks: ${stockPct}% · Bonds: ${bondPct}% · Expected Return: ~${expectedReturn}%`}
            action={<div />}
          />
          <Slider value={[stockPct]} min={10} max={100} step={5} onValueChange={([v]) => setStockPct(v)} />
          <div className="flex justify-between text-xs text-muted-foreground"><span>Conservative</span><span>Aggressive</span></div>
          <div className="rounded-2xl border border-border bg-card/50 p-4 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Risk Profile</p>
            <p className={`font-heading font-bold text-lg ${stockPct > 80 ? "text-destructive" : stockPct > 50 ? "text-primary" : "text-accent"}`}>{riskLevel}</p>
          </div>
          <Button onClick={() => setStep("risk_return")} className="w-full gap-2">Next: Risk & Return <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "risk_return" && (
        <ModuleStepCard
          icon={<TrendingUp className="w-8 h-8 text-accent" />}
          title="Risk vs Return Tradeoff"
          body="Higher expected returns always come with higher risk. The key is matching your allocation to your time horizon. Young investors can tolerate more volatility. Near-retirees need stability."
          highlights={[
            "20+ years to retirement → more stocks",
            "5-10 years → balanced mix",
            "Under 5 years → preserve capital",
            "Rebalance annually to maintain your target",
          ]}
          action={<Button onClick={() => setStep("tax_efficiency")} className="w-full gap-2">Next: Tax Efficiency <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "tax_efficiency" && (
        <ModuleStepCard
          icon={<Layers className="w-8 h-8 text-primary" />}
          title="Tax-Efficient Placement"
          body="Where you hold assets matters as much as what you hold. Tax-inefficient assets (bonds, REITs) belong in tax-advantaged accounts. Tax-efficient assets (index funds) can go in taxable accounts."
          highlights={[
            "Bonds → 401(k) or Traditional IRA",
            "REITs → Tax-sheltered accounts",
            "Index funds → Taxable brokerage is fine",
            "Tax-loss harvesting in taxable accounts",
          ]}
          action={<Button onClick={() => setStep("preservation")} className="w-full gap-2">Next: Capital Preservation <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "preservation" && (
        <ModuleStepCard
          icon={<Shield className="w-8 h-8 text-accent" />}
          title="Capital Preservation"
          body="Growing wealth means nothing if you can't keep it. Preservation strategies include maintaining an emergency fund, avoiding concentrated positions, and building a margin of safety into every decision."
          highlights={[
            "Never invest money you'll need within 5 years",
            "Diversification is your primary defense",
            "Emergency fund = insurance for your investments",
            "Avoid sequence-of-returns risk near retirement",
          ]}
          action={<Button onClick={() => setStep("scenario")} className="w-full gap-2">Next: Scenario Planning <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "scenario" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<Target className="w-8 h-8 text-primary" />} title="Scenario: Market Drops 30%" body="Markets just crashed 30%. What's your move?" action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "stay", label: "Stay the Course", desc: "Don't sell. Rebalance if needed. History shows recovery. Time in market > timing market." },
              { key: "buy", label: "Buy More", desc: "Deploy cash reserves into cheaper assets. Contrarian but historically rewarding." },
              { key: "sell", label: "Sell to Cash", desc: "Panic selling locks in losses. You'd need to time re-entry perfectly — almost impossible." },
            ].map(s => (
              <button key={s.key} className={`rounded-2xl border p-4 text-left transition-all ${scenarioChoice === s.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setScenarioChoice(s.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{s.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.desc}</p>
              </button>
            ))}
          </div>
          {scenarioChoice === "sell" && (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
              <p className="text-xs text-destructive">⚠️ Selling during a crash is the most common wealth-destroying behavior. Historically, staying invested beats panic selling nearly 100% of the time.</p>
            </div>
          )}
          <Button onClick={() => setStep("quiz")} disabled={!scenarioChoice} className="w-full gap-2">Take Knowledge Check <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "quiz" && (
        <ModuleQuiz moduleKey="wealth_strategy" onPass={() => { setCompleted(true); setStep("summary"); completeModule("wealth_strategy"); }} />
      )}

      {step === "summary" && (
        <div className="space-y-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground">Wealth Strategy Complete</h2>
          <p className="text-sm text-muted-foreground">You've mastered allocation, tax efficiency, and preservation. Build wealth with discipline.</p>
          <Button onClick={() => navigate("/dashboard")} className="w-full">Back to Dashboard</Button>
        </div>
      )}
    </ModuleShell>
  );
};

export default WealthStrategyModule;
